import React, { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Eye, Grid, List } from 'lucide-react'
import ProductCard from '../cards/product-card'
import { ProductGridSkeleton } from '../cards/product-card-skeleton'
import { FilteredProductsResponse } from '@/entities/api/products/product-filters'
import useResponsiveSkeletonCount from '@/hooks/useResponsiveSkeletonCount'
import { useNavigate } from 'react-router-dom'
import CustomersEntity from '@/entities/api/customers/customer'

interface ProductSliderProps {
	title?: string
	category?: string
	showViewToggle?: boolean
	filteredProducts?: FilteredProductsResponse
	useExternalPagination?: boolean
	onPageChange?: (page: number, limit: number) => void
	onLimitChange?: (limit: number) => void
	isExternalLoading?: boolean
	goZone?: (data?: any) => void
	haveZone?: boolean
	customerId: string
	customer: CustomersEntity | null
}

const ProductSlider: React.FC<ProductSliderProps> = ({
	title = 'Productos Destacados',
	category = 'general',
	showViewToggle = true,
	filteredProducts,
	useExternalPagination = false,
	onPageChange,
	onLimitChange,
	goZone,
	isExternalLoading = false,
	haveZone = true,
	customerId,
	customer,
}) => {
	const [currentPage, setCurrentPage] = useState(1)
	const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
	const [isLoading, setIsLoading] = useState(false)
	const [showMobileControls] = useState(false)
	const [productsPerPage, setProductsPerPage] = useState(12)
	const navigate = useNavigate()

	const skeletonCount = useResponsiveSkeletonCount({ viewMode })

	const calculateProductsPerPage = () => {
		const width = window.innerWidth

		if (width >= 1280) {
			return viewMode === 'grid' ? 12 : 8
		} else if (width >= 1024) {
			return viewMode === 'grid' ? 9 : 6
		} else if (width >= 768) {
			return viewMode === 'grid' ? 8 : 4
		} else if (width >= 640) {
			return viewMode === 'grid' ? 6 : 4
		} else {
			return 4
		}
	}

	useEffect(() => {
		const updateProductsPerPage = () => {
			const newLimit = calculateProductsPerPage()
			setProductsPerPage(newLimit)

			if (useExternalPagination && onLimitChange) {
				onLimitChange(newLimit)
			}
		}

		updateProductsPerPage()

		window.addEventListener('resize', updateProductsPerPage)

		return () => {
			window.removeEventListener('resize', updateProductsPerPage)
		}
	}, [viewMode, useExternalPagination, onLimitChange])

	useEffect(() => {
		const newLimit = calculateProductsPerPage()
		setProductsPerPage(newLimit)

		if (useExternalPagination && onLimitChange) {
			onLimitChange(newLimit)
		}
	}, [viewMode, useExternalPagination, onLimitChange])

	const totalProducts = filteredProducts?.totalCount || 0
	const totalPages = useExternalPagination
		? filteredProducts?.totalPages || 0
		: Math.ceil(totalProducts / productsPerPage) > 2
		? Math.ceil(totalProducts / productsPerPage) - 1
		: Math.ceil(totalProducts / productsPerPage)

	const currentPageNumber = useExternalPagination
		? filteredProducts?.currentPage || 1
		: currentPage

	const startIndex = useExternalPagination
		? (currentPageNumber - 1) * (filteredProducts?.limit || productsPerPage)
		: (currentPage - 1) * productsPerPage

	const endIndex = useExternalPagination
		? startIndex + (filteredProducts?.limit || productsPerPage)
		: startIndex + productsPerPage

	const currentProducts = useExternalPagination
		? filteredProducts?.products || []
		: filteredProducts?.products?.slice(startIndex, endIndex) || []

	const handlePageChange = (page: number) => {
		if (page >= 1 && page <= totalPages) {
			if (useExternalPagination) {
				if (onPageChange) {
					onPageChange(page, productsPerPage)
				}
			} else {
				setIsLoading(true)
				setCurrentPage(page)

				setTimeout(() => {
					setIsLoading(false)
				}, 500)
			}
		}
	}

	useEffect(() => {
		if (!useExternalPagination) {
			setCurrentPage(1)
		}
	}, [productsPerPage, viewMode, useExternalPagination])

	const showLoading = useExternalPagination ? isExternalLoading : isLoading

	return (
		<div className='w-full'>
			<div className='relative mb-3 md:mb-5'>
				<div className='relative bg-gradient-to-r from-white via-gray-50 to-white rounded-lg md:rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
					<div className='absolute left-0 top-0 bottom-0 w-1 md:w-1.5 bg-gradient-to-b from-red-500 to-red-600'></div>

					<div className='p-2 sm:p-2 md:p-3 lg:p-4'>
						<div className='p-3 sm:p-2 md:p-2 lg:p-2'>
							<div className='flex flex-col space-y-3 sm:space-y-3 md:space-y-0 md:flex-row md:items-start md:justify-between'>
								<div className='flex items-start space-x-3 sm:space-x-3 md:space-x-4 flex-1 min-w-0'>
									<div className='p-2 sm:p-2 md:p-2.5 bg-red-50 rounded-xl md:rounded-xl flex-shrink-0 shadow-sm'>
										<div className='w-6 h-6 sm:w-6 sm:h-6 md:w-7 md:h-7 bg-gradient-to-br from-red-500 to-red-600 rounded-lg md:rounded-lg flex items-center justify-center shadow-sm'>
											<span className='text-white text-sm sm:text-sm md:text-xs font-bold'>
												🏷️
											</span>
										</div>
									</div>

									<div className='flex-1 min-w-0 pt-0.5'>
										<h2 className='text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-gray-800 leading-tight mb-1'>
											<span className='block truncate'>{title}</span>
										</h2>
										<p className='text-sm sm:text-sm md:text-sm text-gray-600 flex items-center'>
											<span className='w-2 h-2 sm:w-2 sm:h-2 bg-green-400 rounded-full mr-2 flex-shrink-0 animate-pulse'></span>
											<span className='truncate font-medium'>
												{totalProducts} productos disponibles
											</span>
										</p>
									</div>
								</div>
							</div>
						</div>

						<div
							className={`
              md:hidden overflow-hidden transition-all duration-300 ease-in-out
              ${
								showMobileControls
									? 'max-h-96 opacity-100 mt-2'
									: 'max-h-0 opacity-0'
							}
            `}
						>
							<div className='space-y-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200'>
								{showViewToggle && (
									<div className='space-y-1.5'>
										<label className='text-xs font-semibold text-gray-700 uppercase tracking-wide'>
											Tipo de vista
										</label>
										<div className='flex items-center bg-white rounded-lg p-0.5 border border-gray-200'>
											<button
												onClick={() => setViewMode('grid')}
												className={`flex-1 flex items-center justify-center space-x-1.5 p-1.5 rounded-md transition-all duration-200 ${
													viewMode === 'grid'
														? 'bg-red-500 text-white shadow-sm'
														: 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
												}`}
												aria-label='Vista en cuadrícula'
											>
												<Grid size={14} />
												<span className='text-sm font-medium'>Cuadrícula</span>
											</button>
											<button
												onClick={() => setViewMode('list')}
												className={`flex-1 flex items-center justify-center space-x-1.5 p-1.5 rounded-md transition-all duration-200 ${
													viewMode === 'list'
														? 'bg-red-500 text-white shadow-sm'
														: 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
												}`}
												aria-label='Vista en lista'
											>
												<List size={14} />
												<span className='text-sm font-medium'>Lista</span>
											</button>
										</div>
									</div>
								)}
							</div>
						</div>

						<div className='hidden md:flex md:items-center md:justify-between md:mt-4'>
							<div className='text-sm ml-2 md:ml-4 lg:text-sm text-gray-500'>
								Mostrando productos de la categoría{' '}
								<span className='font-medium text-gray-700'>{category}</span>
							</div>

							<div className='flex items-center space-x-2.5 lg:space-x-3'>
								{showViewToggle && (
									<div className='flex items-center bg-gray-100 rounded-lg p-0.5'>
										<button
											onClick={() => setViewMode('grid')}
											className={`p-1.5 rounded-md transition-all duration-200 ${
												viewMode === 'grid'
													? 'bg-white text-red-600 shadow-sm'
													: 'text-gray-600 hover:text-gray-800'
											}`}
											aria-label='Vista en cuadrícula'
										>
											<Grid size={16} />
										</button>
										<button
											onClick={() => setViewMode('list')}
											className={`p-1.5 rounded-md transition-all duration-200 ${
												viewMode === 'list'
													? 'bg-white text-red-600 shadow-sm'
													: 'text-gray-600 hover:text-gray-800'
											}`}
											aria-label='Vista en lista'
										>
											<List size={16} />
										</button>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='relative mb-4'>
				{showLoading ? (
					<ProductGridSkeleton
						count={skeletonCount}
						showVariants={true}
						viewMode={viewMode}
						className='animate-pulse'
					/>
				) : (
					<div
						className={`
			  grid transition-all duration-300
			  ${
					viewMode === 'grid'
						? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-5'
						: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-3 md:gap-4 lg:gap-5'
				}
		`}
					>
						{currentProducts.map((filteredProduct, index) => (
							<ProductCard
								click={() =>
									navigate(
										`/Producto/${filteredProduct.product?.name.replace(
											/ /g,
											'-'
										)}/${filteredProduct.product._id}`
									)
								}
								key={filteredProduct.product._id || index}
								product={filteredProduct.product}
								customer={customer}
								customerId={customerId}
							/>
						))}
					</div>
				)}
			</div>

			{totalPages > 0 && (
				<div className='bg-gradient-to-r from-gray-50 to-white rounded-lg md:rounded-xl border border-gray-200 p-3 md:p-5 lg:p-6'>
					<div className='flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-2 lg:space-y-0'>
						<div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3'>
							<div className='text-sm text-gray-600'>
								Página{' '}
								<span className='font-bold text-gray-800'>
									{currentPageNumber}
								</span>{' '}
								de <span className='font-bold'>{totalPages}</span>
							</div>
							<div className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full w-fit'>
								{startIndex + 1}-{Math.min(endIndex, totalProducts)} de{' '}
								{totalProducts}
							</div>
							{useExternalPagination && (
								<div className='text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full w-fit'>
									{productsPerPage} por página
								</div>
							)}
						</div>

						<div className='flex items-center space-x-2'>
							<div className='flex items-center space-x-1'>
								<button
									onClick={() => handlePageChange(currentPageNumber - 1)}
									disabled={currentPageNumber === 1 || showLoading}
									className='p-1.5 md:p-2 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 group'
									aria-label='Página anterior'
								>
									<ChevronLeft
										size={16}
										className='md:w-[18px] md:h-[18px] text-gray-600 group-hover:text-gray-800'
									/>
								</button>

								<div className='hidden sm:flex items-center space-x-1'>
									{[...Array(Math.min(5, totalPages))].map((_, index) => {
										let pageNumber: number

										if (totalPages <= 5) {
											pageNumber = index + 1
										} else {
											const startPage = Math.max(1, currentPageNumber - 2)
											const endPage = Math.min(totalPages, startPage + 4)
											pageNumber = startPage + index

											if (pageNumber > endPage) return null
										}

										const isActive = pageNumber === currentPageNumber

										return (
											<button
												key={pageNumber}
												onClick={() => handlePageChange(pageNumber)}
												disabled={showLoading}
												className={`w-8 h-8 md:w-9 md:h-9 rounded-lg font-medium text-sm transition-all duration-200 ${
													isActive
														? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
														: 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
												}`}
											>
												{pageNumber}
											</button>
										)
									})}
								</div>

								<button
									onClick={() => handlePageChange(currentPageNumber + 1)}
									disabled={currentPageNumber === totalPages || showLoading}
									className='p-1.5 md:p-2 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 group'
									aria-label='Página siguiente'
								>
									<ChevronRight
										size={16}
										className='md:w-[18px] md:h-[18px] text-gray-600 group-hover:text-gray-800'
									/>
								</button>
							</div>

							{haveZone && (
								<button
									onClick={() => goZone?.()}
									disabled={showLoading}
									className='flex items-center space-x-1 cursor-pointer md:space-x-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold px-3 py-2 md:px-5 md:py-2.5 rounded-lg md:rounded-xl shadow-lg hover:shadow-red-500/30 transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group'
								>
									<Eye
										size={14}
										className='md:w-4 md:h-4 group-hover:scale-110 transition-transform duration-200'
									/>
									<span className='text-xs md:text-sm'>Ver más</span>
								</button>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default ProductSlider
